import {
  IonAccordion,
  IonAccordionGroup,
  IonButton,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonSpinner,
  IonText,
  isPlatform,
  useIonAlert,
} from '@ionic/react';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import {
  checkmarkCircleOutline,
  closeCircle,
  lockClosed,
  lockOpenOutline,
} from 'ionicons/icons';
import { isEmpty, isNaN, orderBy } from 'lodash';
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';
import { useHasFeature } from '../../../../../../api/hooks/feature.hooks';
import { ActionButton } from '../../../../../../components/button/action-button';
import { RightAlignedButtons } from '../../../../../../components/button/aligned-buttons';
import { BaseButton } from '../../../../../../components/button/base-button';
import { CloseButton } from '../../../../../../components/button/close-button';
import { IntercomHelp } from '../../../../../../components/help/intercom-help';
import { IntercomArticle } from '../../../../../../config/intercom';
import i18n from '../../../../../../i18n';
import { getLocalizedText } from '../../../../../../i18n/i18n.utils';
import { QNear, Waypoint } from '../../../../../../util/machine-variable.utils';
import { PoseUtil } from '../../../../../../util/pose.util';
import { useFlowServices } from '../../../hook/flow-service.hooks';
import {
  WaypointCaptureRef,
  useWaypointCapture,
} from '../../../hook/waypoint-capture.hooks';
import {
  FlowItem,
  FlowItemSharedParameterRef,
  FlowItemSharedRef,
  FlowItemText,
  SystemUnit,
} from '../../../model/flow.types';
import { FlowAnalyticService } from '../../../service/flow-analytic.service';
import {
  instanceOfFlowItemSectionParent,
  instanceOfPlaceholder,
  processItemsForSave,
} from '../../../util/flow-item.util';
import { getParameterValue } from '../../../util/flow-parameter.util';
import { convertUnits } from '../../../util/math';
import { useFlowRefContext } from '../../flow/flow-ref';
import { NumericInputWidget } from '../../widget/numeric-input';
import { PickerInput } from '../../widget/picker-input';
import { PickerOption } from '../../widget/widget.types';
import './flow-item-shift.css';

export interface FlowItemShiftProps {
  dismiss?: () => void;
}

export interface LockedAxies {
  x: boolean;
  y: boolean;
  z: boolean;
}

interface OverrideAxies {
  x: number | undefined;
  y: number | undefined;
  z: number | undefined;
}

enum SaveType {
  SHIFT_IN_PLACE,
  SHIFT_AND_COPY,
}

enum CaptureType {
  NEW_ORIGIN_WAYPOINT,
  MOVE_WAYPOINT,
}

interface RobotPosition {
  waypoint: Waypoint;
  qnear: QNear;
}

interface SelectedItemDetail {
  item: FlowItem;
  label?: FlowItemText;
  order: number;
}

interface FormData {
  originItemDetail: SelectedItemDetail;
  newRobotPosition?: RobotPosition;
  overrideAxies?: {
    waypoint?: OverrideAxies;
  };
  rotationAngle: number;
}

const schema = yup.object().shape({
  originItemDetail: yup.object().required('*'),
  newRobotPosition: yup.object().shape({
    waypoint: yup.object().shape({
      x: yup.number().required(),
      y: yup.number().required(),
      z: yup.number().required(),
      rx: yup.number().required(),
      ry: yup.number().required(),
      rz: yup.number().required(),
    }),
    qnear: yup.array().required(),
  }),
  overrideAxies: yup.object().shape({
    waypoint: yup.object().shape({
      x: yup.number(),
      y: yup.number(),
      z: yup.number(),
    }),
  }),
  rotationAngle: yup.number().min(-180).max(180),
});

const FlowItemShift = (props: FlowItemShiftProps) => {
  const { dismiss } = props;

  const {
    flowItemTextArr,
    flowItems,
    selectedItemDetails: { selectedFlowItemsPersisted: selectedItems },
    addItemsToFlow,
    onUpdateItems,
  } = useFlowRefContext();

  const [isShiftAndCopying, setShiftAndCopying] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [lockedAxies, setLockedAxis] = useState<LockedAxies>({
    x: false,
    y: false,
    z: false,
  });

  const saveTypeRef = useRef<SaveType>(SaveType.SHIFT_IN_PLACE);
  const captureTypeRef = useRef<CaptureType>(CaptureType.NEW_ORIGIN_WAYPOINT);
  const moveWaypointRef = useRef<RobotPosition | undefined>();

  const formikRef = useRef<FormikProps<FormData> | null>(null);

  const [currentRobotPosition, setCurrentRobotPosition] =
    useState<RobotPosition>();

  const { flowAnalyticService, flowItemService, system } = useFlowServices();
  const hasPartRotationFeature = useHasFeature<boolean>('beacon-part-rotation');

  const sortedItemsAll: SelectedItemDetail[] = useMemo(() => {
    const items: SelectedItemDetail[] = [];

    for (let i = 0; i < selectedItems.length; i++) {
      const item = selectedItems[i];

      for (let j = 0; j < flowItems?.length; j++) {
        const el = flowItems[j];
        if (!instanceOfPlaceholder(flowItems[j])) {
          if ((el as FlowItem).id === item.id) {
            items.push({
              item,
              label: flowItemTextArr[j],
              order: j,
            });
          }
        }
      }
    }

    return orderBy(items, 'order');
  }, [selectedItems, flowItems, flowItemTextArr]);

  const sortedItemsFilteredForOrigin: SelectedItemDetail[] = useMemo(() => {
    return sortedItemsAll.filter((i) =>
      flowItemService.isShiftableItem(i.item)
    );
  }, [sortedItemsAll, flowItemService]);

  const initialValues: FormData = useMemo(() => {
    return {
      originItemDetail: sortedItemsFilteredForOrigin[0] ?? undefined,
      rotationAngle: 0,
    };
  }, [sortedItemsFilteredForOrigin]);

  const originPickerOptions = useMemo((): PickerOption[] => {
    return sortedItemsFilteredForOrigin.map((i) => {
      return {
        label: i.label?.label ?? i.item.name ?? i18n.t('common:unknown'),
        value: i as unknown as Record<string, unknown>,
      };
    });
  }, [sortedItemsFilteredForOrigin]);

  const [moveWaypointAlert, dismissMoveWaypointAlert] = useIonAlert();

  const onFormActionClicked = useCallback(
    (saveType: SaveType) => {
      saveTypeRef.current = saveType;

      if (saveType === SaveType.SHIFT_AND_COPY) {
        captureTypeRef.current = CaptureType.MOVE_WAYPOINT;
        moveWaypointAlert({
          message: i18n.t('common:shift_move_waypoint_help_text'),
          buttons: [
            {
              text: i18n.t('common:shift_move_waypoint_skip'),
              handler: () => {
                formikRef.current?.submitForm();
              },
            },
          ],
          backdropDismiss: false,
        });
      } else {
        formikRef.current?.submitForm();
      }
    },
    [moveWaypointAlert]
  );

  const onSubmit = useCallback(
    async (form: FormData) => {
      // immediately reset capture type after submitting the form
      captureTypeRef.current = CaptureType.NEW_ORIGIN_WAYPOINT;

      if (!form.newRobotPosition) {
        return;
      }

      await setSaving(true);

      const orginWaypoint = getParameterValue<Waypoint>(
        form.originItemDetail.item,
        FlowItemSharedParameterRef.WAYPOINT
      );

      const overriddenValueNative = getConvertedOverrideValueToNative(
        form.overrideAxies?.waypoint,
        system
      );

      const modifiedNewOrigin = getModifiedWaypoint(
        orginWaypoint,
        form.newRobotPosition.waypoint,
        lockedAxies,
        overriddenValueNative
      );

      const originQnear = getParameterValue<QNear>(
        form.originItemDetail.item,
        FlowItemSharedParameterRef.QNEAR
      );

      const modifiedNewOriginQnear = form.newRobotPosition.qnear;

      const flowItems = sortedItemsAll.map((i) => i.item);
      const rotationAngle = form.rotationAngle;

      let shifted = flowItemService.shiftFlowItems(
        flowItems,
        orginWaypoint,
        modifiedNewOrigin,
        originQnear,
        modifiedNewOriginQnear,
        rotationAngle
      );

      if (saveTypeRef.current === SaveType.SHIFT_AND_COPY) {
        if (shifted.some((i) => instanceOfFlowItemSectionParent(i))) {
          shifted = flowItemService.getCopyWithNestedItems(shifted);
        }

        // delete the item ids so these are treated as new items and inserted at the end of the
        // existing flow items. add a section ahead of it.
        shifted.forEach((i) => {
          i.id = undefined;
        });

        const processedItems = processItemsForSave(shifted);

        const section = flowItemService.getDefaultItemForRef(
          FlowItemSharedRef.SECTION
        );

        section.id = uuid();
        section.name = getLocalizedText('common:pattern');

        const additionalItemsToAdd = [section];

        if (moveWaypointRef.current) {
          const moveItem = flowItemService.getDefaultItemForRef(
            FlowItemSharedRef.MOVE
          );

          moveItem.id = uuid();

          flowItemService.setParameterValue(
            moveItem,
            FlowItemSharedParameterRef.WAYPOINT,
            moveWaypointRef.current.waypoint
          );

          flowItemService.setParameterValue(
            moveItem,
            FlowItemSharedParameterRef.QNEAR,
            moveWaypointRef.current.qnear
          );

          additionalItemsToAdd.push(moveItem);
        }

        await addItemsToFlow(additionalItemsToAdd.concat(processedItems));

        // reset the origin point so a user can continue shifting aka patterning
        if (formikRef.current) {
          formikRef.current.setFieldValue('newRobotPosition', undefined);
        }

        setShiftAndCopying(true);
      } else if (saveTypeRef.current === SaveType.SHIFT_IN_PLACE) {
        await onUpdateItems(shifted);
      }

      await setSaving(false);

      // empty out the move waypoint since we have consumed it
      moveWaypointRef.current = undefined;

      if (saveTypeRef.current === SaveType.SHIFT_IN_PLACE && dismiss) {
        dismiss();
      }
    },
    [
      system,
      lockedAxies,
      sortedItemsAll,
      flowItemService,
      dismiss,
      addItemsToFlow,
      onUpdateItems,
    ]
  );

  const onNewRobotPositionCapture = useCallback(
    (waypoint: Waypoint, qnear: QNear) => {
      if (formikRef.current) {
        const position: RobotPosition = {
          waypoint,
          qnear,
        };

        if (captureTypeRef.current === CaptureType.MOVE_WAYPOINT) {
          moveWaypointRef.current = position;
          formikRef.current.submitForm();
          dismissMoveWaypointAlert();
          flowAnalyticService.trackShiftCapturedAirMove();
        } else {
          formikRef.current.setFieldValue('newRobotPosition', position);
        }
      }
    },
    [dismissMoveWaypointAlert, flowAnalyticService]
  );

  const waypointCaptureRef = useRef<WaypointCaptureRef>();

  useWaypointCapture({
    onCapture: onNewRobotPositionCapture,
    innerRef: waypointCaptureRef,
  });

  useEffect(() => {
    const getAndSetNewPosition = () => {
      const position =
        waypointCaptureRef.current?.realtimePosition?.getLastPosition();

      if (position?.waypoint) {
        setCurrentRobotPosition({
          waypoint: position.waypoint,
          qnear: position.jointPositions,
        });
      }
    };

    const interval = setInterval(() => {
      getAndSetNewPosition();
    }, 500);

    getAndSetNewPosition();

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  const onShiftAndCopy = useCallback(
    (e: MouseEvent<HTMLElement>, isOriginInitialItem: boolean) => {
      e.preventDefault();
      onFormActionClicked(SaveType.SHIFT_AND_COPY);
      flowAnalyticService.trackShiftAndCopy({
        lockedAxies,
        numOfItems: selectedItems.length,
        isOriginInitialItem,
      });
    },
    [
      flowAnalyticService,
      lockedAxies,
      onFormActionClicked,
      selectedItems.length,
    ]
  );

  const captureNeededText = isShiftAndCopying
    ? i18n.t('common:shift_help_text_capture_again')
    : i18n.t('common:shift_help_text_capture_required');

  return (
    <div className={'flow-panel-container'}>
      <IonListHeader className={'panel-header'}>
        <IonLabel>
          {i18n.t('common:shift_points', {
            numOfItems: selectedItems.length,
          })}
        </IonLabel>
        <IonButton
          color="primary"
          onClick={() => {
            if (waypointCaptureRef.current) {
              waypointCaptureRef.current.forcePress();
            }
          }}
        >
          {i18n.t('common:capture')}
        </IonButton>
        <IntercomHelp articleId={IntercomArticle.SHIFTING} />
      </IonListHeader>
      <div className="header-divider" />
      <Formik
        validationSchema={schema}
        enableReinitialize={true}
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={(instance) => (formikRef.current = instance)}
        validateOnMount={true}
      >
        {({ errors, values }) => {
          const isReadyToSubmit = isEmpty(errors);
          const originItemDetail = values.originItemDetail;
          const originWaypoint = getParameterValue<Waypoint>(
            originItemDetail.item,
            FlowItemSharedParameterRef.WAYPOINT
          );

          const isOriginInitialItem =
            sortedItemsFilteredForOrigin.findIndex(
              (i) => i.item.id === originItemDetail.item.id
            ) === 0;

          return (
            <Form>
              <div className="shift-container">
                <PickerInput
                  label={i18n.t('common:origin')}
                  name="originItemDetail"
                  options={originPickerOptions}
                />
                {hasPartRotationFeature && (
                  <NumericInputWidget
                    name="rotationAngle"
                    label={i18n.t('common:rotation_angle')}
                  />
                )}
                <div className="shift-capture-wrapper ion-text-center">
                  <IonText>
                    <p>
                      {values.newRobotPosition
                        ? i18n.t('common:shift_help_text_capture_complete')
                        : captureNeededText}
                    </p>
                  </IonText>
                  {values.newRobotPosition ? (
                    <IonIcon
                      icon={checkmarkCircleOutline}
                      color="success"
                      size="large"
                    />
                  ) : (
                    <IonSpinner />
                  )}
                </div>
                {originWaypoint && (
                  <RobotPosition
                    flowAnalyticService={flowAnalyticService}
                    originWaypoint={originWaypoint}
                    newOriginWaypoint={currentRobotPosition?.waypoint}
                    system={system}
                    lockedAxis={lockedAxies}
                    onLockedAxisChange={(nextLockedAxis) => {
                      setLockedAxis(nextLockedAxis);
                    }}
                  />
                )}
              </div>
              <div style={{ padding: 8 }} />
              <RightAlignedButtons>
                <CloseButton
                  label={
                    isShiftAndCopying
                      ? i18n.t('common:done')
                      : i18n.t('common:cancel')
                  }
                  onClose={() => {
                    if (typeof dismiss === 'function') {
                      dismiss();
                    }
                  }}
                />
                {isShiftAndCopying ? (
                  <BaseButton
                    buttonProps={{
                      disabled: !isReadyToSubmit || saving,
                      onClick: (e) => {
                        onShiftAndCopy(e, isOriginInitialItem);
                      },
                    }}
                  >
                    {getLocalizedText('common:shift_points_and_copy')}
                  </BaseButton>
                ) : (
                  <ActionButton
                    label={getLocalizedText('common:apply')}
                    buttonProps={{
                      color: isReadyToSubmit ? 'primary' : 'medium',
                      disabled: !isReadyToSubmit || saving,
                      fill: isReadyToSubmit ? 'solid' : 'outline',
                    }}
                  >
                    {({ close }) => {
                      return (
                        <IonList lines="full">
                          <IonItem
                            onClick={(e) => {
                              e.preventDefault();
                              onFormActionClicked(SaveType.SHIFT_IN_PLACE);
                              flowAnalyticService.trackShiftInPlace({
                                lockedAxies,
                                numOfItems: selectedItems.length,
                                isOriginInitialItem,
                              });
                              close();
                            }}
                            href="#"
                            detail={false}
                          >
                            {getLocalizedText('common:shift_points_in_place')}
                          </IonItem>
                          <IonItem
                            lines="none"
                            onClick={(e) => {
                              onShiftAndCopy(e, isOriginInitialItem);
                              close();
                            }}
                            href="#"
                            detail={false}
                          >
                            {getLocalizedText('common:shift_points_and_copy')}
                          </IonItem>
                        </IonList>
                      );
                    }}
                  </ActionButton>
                )}
              </RightAlignedButtons>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

interface RobotPositionProps {
  flowAnalyticService: FlowAnalyticService;
  originWaypoint: Waypoint;
  newOriginWaypoint?: Waypoint;
  system: SystemUnit;
  lockedAxis: LockedAxies;
  onLockedAxisChange?: (lockedAxis: LockedAxies) => void;
}

const RobotPosition = (props: RobotPositionProps) => {
  const {
    flowAnalyticService,
    originWaypoint,
    newOriginWaypoint,
    system,
    lockedAxis,
    onLockedAxisChange,
  } = props;

  const { values, setFieldValue } = useFormikContext<FormData>();

  const result = useMemo(() => {
    let x: number | undefined;
    let y: number | undefined;
    let z: number | undefined;
    let distance: number | undefined;

    if (newOriginWaypoint) {
      const overriddenValueNative = getConvertedOverrideValueToNative(
        values.overrideAxies?.waypoint,
        system
      );
      const modifiedNewOriginPose = getModifiedWaypoint(
        originWaypoint,
        newOriginWaypoint,
        lockedAxis,
        overriddenValueNative
      );
      x = modifiedNewOriginPose.x - originWaypoint.x;
      y = modifiedNewOriginPose.y - originWaypoint.y;
      z = modifiedNewOriginPose.z - originWaypoint.z;
      distance = PoseUtil.point_dist(
        [
          originWaypoint.x,
          originWaypoint.y,
          originWaypoint.z,
          originWaypoint.rx,
          originWaypoint.ry,
          originWaypoint.rz,
        ],
        [
          lockedAxis.x ? originWaypoint.x : modifiedNewOriginPose.x,
          lockedAxis.y ? originWaypoint.y : modifiedNewOriginPose.y,
          lockedAxis.z ? originWaypoint.z : modifiedNewOriginPose.z,
          modifiedNewOriginPose.rx,
          modifiedNewOriginPose.ry,
          modifiedNewOriginPose.rz,
        ]
      );
    }

    return {
      x,
      y,
      z,
      distance,
    };
  }, [
    newOriginWaypoint,
    values.overrideAxies?.waypoint,
    system,
    originWaypoint,
    lockedAxis,
  ]);

  return (
    <IonAccordionGroup
      onIonChange={(e) => {
        if (e.detail.value !== undefined) {
          flowAnalyticService.trackShiftExpandedAxisDetail();
        }
      }}
    >
      <IonAccordion toggleIconSlot="end">
        <IonItem lines={'full'} slot="header">
          <IonLabel>{i18n.t('common:distance_from_origin')}</IonLabel>
          <IonLabel slot="end">
            {getConvertedValueText(result.distance, system)}{' '}
            {getConversionUom(system)}
          </IonLabel>
        </IonItem>
        <div slot="content">
          <AxisPosition
            axis="X"
            value={result.x}
            isLocked={lockedAxis.x}
            system={system}
            overriddenValue={values.overrideAxies?.waypoint?.x}
            onToggleLock={() => {
              if (onLockedAxisChange) {
                onLockedAxisChange({ ...lockedAxis, x: !lockedAxis.x });
              }
            }}
            onSetOverride={(value) => {
              setFieldValue('overrideAxies.waypoint.x', value);
            }}
          />
          <AxisPosition
            axis="Y"
            value={result.y}
            isLocked={lockedAxis.y}
            system={system}
            onToggleLock={() => {
              if (onLockedAxisChange) {
                onLockedAxisChange({ ...lockedAxis, y: !lockedAxis.y });
              }
            }}
            overriddenValue={values.overrideAxies?.waypoint?.y}
            onSetOverride={(value) => {
              setFieldValue('overrideAxies.waypoint.y', value);
            }}
          />
          <AxisPosition
            axis="Z"
            value={result.z}
            isLast={true}
            isLocked={lockedAxis.z}
            system={system}
            onToggleLock={() => {
              if (onLockedAxisChange) {
                onLockedAxisChange({ ...lockedAxis, z: !lockedAxis.z });
              }
            }}
            overriddenValue={values.overrideAxies?.waypoint?.z}
            onSetOverride={(value) => {
              setFieldValue('overrideAxies.waypoint.z', value);
            }}
          />
        </div>
      </IonAccordion>
    </IonAccordionGroup>
  );
};

interface AxisPositionProps {
  axis: 'X' | 'Y' | 'Z';
  value?: number;
  isLast?: boolean;
  isLocked: boolean;
  overriddenValue: number | undefined;
  system: SystemUnit;
  onToggleLock: () => void;
  onSetOverride: (value: number | undefined) => void;
}

const AxisPosition = (props: AxisPositionProps) => {
  const {
    axis,
    isLast,
    isLocked,
    overriddenValue,
    system,
    onToggleLock,
    onSetOverride,
  } = props;

  const [isFocused, setFocused] = useState<boolean>(false);
  const [overrideString, setOverrideString] = useState<string | undefined>(
    undefined
  );

  let displayValue: string | number | undefined = getConvertedValueText(
    props.value,
    system
  );
  if (isFocused) {
    displayValue = overrideString;
  } else if (overriddenValue !== undefined) {
    displayValue = overriddenValue;
  }

  const inputRef = useRef<HTMLIonInputElement>(null);

  const blurInput = useCallback(async () => {
    const el = await inputRef.current?.getInputElement();
    el?.blur();
  }, []);

  const selectInput = useCallback(async () => {
    if (isPlatform('capacitor')) {
      const el = await inputRef.current?.getInputElement();
      el?.select();
    }
  }, []);

  const showLockIcon = isLocked || overriddenValue !== undefined ? true : false;

  return (
    <IonItem
      lines={isLast ? 'full' : 'none'}
      className="item-shift-axis-position"
    >
      <IonInput
        ref={inputRef}
        label={axis}
        labelPlacement={'start'}
        value={displayValue}
        readonly={isLocked}
        style={{
          textAlign: 'end',
        }}
        placeholder={i18n.t('common:enter_value')}
        onFocus={() => {
          setFocused(true);
          setOverrideString(`${displayValue}`);
          const value = Number(isNaN(displayValue) ? 0 : displayValue);
          onSetOverride(value ?? 0);
          selectInput();
        }}
        onBlur={() => {
          setFocused(false);
        }}
        onIonInput={(e) => {
          const value = e.detail.value;
          setOverrideString(value ?? undefined);
          const convertedValue = Number(value);
          const numValue = isNaN(convertedValue) ? 0 : convertedValue;
          onSetOverride(numValue);
        }}
      />
      <IonLabel slot="end" className="shift-axis-uom-label">
        &nbsp;{getConversionUom(system)}
      </IonLabel>
      {isFocused ? (
        <BaseButton
          buttonProps={{
            slot: 'end',
            fill: 'clear',
            onClick: () => {
              blurInput();
              onSetOverride(undefined);
              setOverrideString(undefined);
            },
          }}
        >
          <IonIcon slot="icon-only" icon={closeCircle} color={'medium'} />
        </BaseButton>
      ) : (
        <BaseButton
          buttonProps={{
            slot: 'end',
            fill: 'clear',
            onClick: () => {
              if (overriddenValue !== undefined) {
                onSetOverride(undefined);
                setOverrideString(undefined);
              } else {
                if (!isLocked) {
                  onSetOverride(0);
                  setOverrideString(`0`);
                }
              }
              onToggleLock();
            },
          }}
        >
          <IonIcon
            slot="icon-only"
            icon={showLockIcon ? lockClosed : lockOpenOutline}
            color={showLockIcon ? 'primary' : 'medium'}
          />
        </BaseButton>
      )}
    </IonItem>
  );
};

const convertSystemValueToNative = (
  value: number | undefined,
  system: SystemUnit
): number | undefined => {
  const prefUom = getPreferredUom(system);
  let nativeValue: number | undefined = undefined;
  if (value !== undefined) {
    nativeValue = convertUnits(value, prefUom, 'm');
  }
  return nativeValue;
};

const getConvertedOverrideValueToNative = (
  overrideWaypoint?: OverrideAxies,
  system?: SystemUnit
): OverrideAxies => {
  if (overrideWaypoint && system) {
    return {
      x: convertSystemValueToNative(overrideWaypoint.x, system),
      y: convertSystemValueToNative(overrideWaypoint.y, system),
      z: convertSystemValueToNative(overrideWaypoint.z, system),
    };
  } else {
    return {
      x: undefined,
      y: undefined,
      z: undefined,
    };
  }
};

const getConvertedValueText = (
  value: number | undefined,
  system: SystemUnit
): string => {
  const prefUom = getPreferredUom(system);
  const convertedValue = convertUnits(value ?? 0, 'm', prefUom, {
    precision: 3,
    notation: 'fixed',
  }).toFixed(3);
  return `${convertedValue}`;
};

const getPreferredUom = (system: SystemUnit): string => {
  return getConversionUom(system);
};

const getConversionUom = (system: SystemUnit): string => {
  if (system === SystemUnit.IMPERIAL) {
    return 'in';
  } else {
    return 'mm';
  }
};

const getModifiedWaypoint = (
  originalPosition: Waypoint,
  currentPosition: Waypoint,
  lockedAxis: LockedAxies,
  overrideAxies?: OverrideAxies
): Waypoint => {
  let newX = lockedAxis.x ? originalPosition.x : currentPosition.x;
  let newY = lockedAxis.y ? originalPosition.y : currentPosition.y;
  let newZ = lockedAxis.z ? originalPosition.z : currentPosition.z;

  // if we're overriding, we need to take the robot native value and adjust the original position
  // for with the (native) ammount entered by the user
  if (overrideAxies?.x !== undefined) {
    newX = originalPosition.x + overrideAxies.x;
  }
  if (overrideAxies?.y !== undefined) {
    newY = originalPosition.y + overrideAxies.y;
  }
  if (overrideAxies?.z !== undefined) {
    newZ = originalPosition.z + overrideAxies.z;
  }

  return {
    x: newX,
    y: newY,
    z: newZ,
    rx: currentPosition.rx,
    ry: currentPosition.ry,
    rz: currentPosition.rz,
  };
};

export { FlowItemShift };
